import React from 'react';
import { clipboardImageFile } from './tableImport';
import { singleCellClipboardValue } from './tableModel';

export type InlineFormatCommand = 'bold' | 'italic' | 'underline' | 'subscript' | 'superscript';

const SHORTCUT_COMMANDS: Record<string, InlineFormatCommand> = {
  b: 'bold',
  i: 'italic',
  u: 'underline',
};

/** Runs an inline-format command on the still-focused cell. Shared by the
 * toolbar buttons and the Ctrl/Cmd+B/I/U shortcuts below. */
export function execInlineFormat(command: InlineFormatCommand): void {
  document.execCommand('styleWithCSS', false, 'false');
  document.execCommand(command);
}

/**
 * Ctrl/Cmd+B/I/U on a `normal` cell: run the matching inline-format command
 * directly (rather than leaving it to the browser's own, inconsistent
 * contenteditable default) and swallow the key. On a styled (header/subHeader)
 * cell — which carries no inline emphasis — just swallow it. Returns whether
 * the key was handled at all.
 */
export function handleInlineFormatShortcut(event: React.KeyboardEvent, styled: boolean): boolean {
  if (!(event.metaKey || event.ctrlKey)) return false;
  const command = SHORTCUT_COMMANDS[event.key.toLowerCase()];
  if (!command) return false;
  event.preventDefault();
  if (!styled) execInlineFormat(command);
  return true;
}

interface CellPasteHandlers {
  onPasteImage: (blob: Blob) => void;
  onPasteTable: (html: string, text: string) => boolean;
}

/**
 * Route a cell paste: an image to the image handler; a single copied cell (still
 * table-shaped clipboard data, but only one cell) straight into this cell instead of
 * the whole-table replace flow — see `singleCellClipboardValue`; anything else
 * (a genuine multi-cell block) to the table handler. preventDefault only when consumed.
 */
export function handleCellPaste(
  event: React.ClipboardEvent,
  { onPasteImage, onPasteTable }: CellPasteHandlers,
): void {
  const image = clipboardImageFile(event.clipboardData);
  if (image) {
    event.preventDefault();
    onPasteImage(image);
    return;
  }
  const html = event.clipboardData.getData('text/html');
  const text = event.clipboardData.getData('text/plain');
  const singleValue = singleCellClipboardValue(html, text);
  if (singleValue !== null) {
    event.preventDefault();
    document.execCommand('insertHTML', false, singleValue);
    return;
  }
  if (onPasteTable(html, text)) event.preventDefault();
}
